// Punktlista — the subscription API.
//
// Only the subscription flow talks to the backend. The catalogue and the
// sample deliveries stay in src/config/rapportvakt.js because they are
// prerendered into the static HTML for search engines, and a fetch would
// remove them from the crawled page.
//
// Every call resolves; none of them throw. A caller gets either a decided
// answer from the server or `offline: true`, which means "we could not ask" —
// the UI keeps its cached state and shows a small note instead of breaking.
//
// Magic-link login (docs/plan/2026-09-11-magic-link-login.md) replaced the
// old "identity is whatever email is in localStorage" model: every call below
// that touches a subscriber's own data now sends a session bearer token
// instead of an email in the body/query, and a 401 means that session is
// gone — the stored copy is forgotten right here, so the account context
// only has to notice on its next render rather than re-implement the check.

import { loadSession, saveSession } from './storage.js';

export const PUNKTLISTA_API_BASE =
  import.meta.env.VITE_PUNKTLISTA_API || 'https://punktlista-production.up.railway.app';

const SUBSCRIPTIONS_URL = `${PUNKTLISTA_API_BASE}/api/v1/subscriptions`;
const SERIES_URL = `${PUNKTLISTA_API_BASE}/api/v1/series`;
const SERIES_REQUESTS_URL = `${PUNKTLISTA_API_BASE}/api/v1/series-requests`;
const AUTH_LINK_URL = `${PUNKTLISTA_API_BASE}/api/v1/auth/link`;
const AUTH_VERIFY_URL = `${PUNKTLISTA_API_BASE}/api/v1/auth/verify`;
const AUTH_ME_URL = `${PUNKTLISTA_API_BASE}/api/v1/auth/me`;
const AUTH_LOGOUT_URL = `${PUNKTLISTA_API_BASE}/api/v1/auth/logout`;
const EDITIONS_URL = `${PUNKTLISTA_API_BASE}/api/v1/editions`;

/** The server writes its refusals in Swedish; they are shown verbatim. */
function detailOf(data, fallback) {
  if (data && typeof data.detail === 'string' && data.detail.trim()) return data.detail.trim();
  return fallback;
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// `url` defaults to the subscriptions endpoint so the two existing call sites
// below are untouched; createOwnFeed is the only caller that points it at
// /series instead. Unauthenticated: /auth/link and /series-requests, the
// only writes left that need no session (see the plan's trust model).
async function send(method, body, url = SUBSCRIPTIONS_URL) {
  let response;
  try {
    response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, offline: true, status: 0, data: null };
  }
  const data = await readJson(response);
  return { ok: response.ok, offline: false, status: response.status, data };
}

/** The bearer header for a signed-in call; empty when there is no session. */
function authHeaders() {
  const session = loadSession();
  return session ? { Authorization: `Bearer ${session.token}` } : {};
}

/**
 * Same contract as send(), but for the endpoints that need the session
 * bearer. A 401 means the session the browser was holding is no longer
 * valid — expired, or signed out elsewhere — so it is forgotten right away;
 * the caller still gets `{ ok: false, status: 401 }` back and is the one that
 * tells the account context to notice (see Shell.jsx's refresh()).
 */
async function authedSend(method, body, url) {
  let response;
  try {
    response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    return { ok: false, offline: true, status: 0, data: null };
  }
  if (response.status === 401) saveSession(null);
  const data = await readJson(response);
  return { ok: response.ok, offline: false, status: response.status, data };
}

async function authedGet(url) {
  let response;
  try {
    response = await fetch(url, { headers: authHeaders() });
  } catch {
    return { ok: false, offline: true, status: 0, data: null };
  }
  if (response.status === 401) saveSession(null);
  const data = await readJson(response);
  return { ok: response.ok, offline: false, status: response.status, data };
}

/** The account's plan when the server's response carries neither field. */
const DEFAULT_TIER = 'free';
const DEFAULT_LIMIT = 1;

/**
 * The signed-in subscriber's own subscriptions. Needs a session; a missing or
 * expired one comes back as `{ ok: false, status: 401, ... }` (see
 * authedGet above) rather than throwing.
 * Returns { ok: true, slugs, rows, tier, limit } or
 * { ok: false, offline, status, slugs: [], rows: [], tier: 'free', limit: 1 }.
 * `slugs` is kept for the existing free-pick reconciliation; `rows` is the
 * raw list (now including owned feeds, `publisher_name: "Egen källa"`) so a
 * caller that needs more than the slug — the frequency, the feed's name —
 * does not have to re-fetch or re-shape anything. `tier`/`limit` are still
 * read off the response as a fallback shape, but the account context (not
 * this response) is the source of truth for them now — see RapportvaktPage.
 */
export async function fetchSubscriptions() {
  const result = await authedGet(SUBSCRIPTIONS_URL);
  if (result.offline) {
    return { ok: false, offline: true, status: 0, slugs: [], rows: [], tier: DEFAULT_TIER, limit: DEFAULT_LIMIT };
  }
  if (!result.ok) {
    return {
      ok: false,
      offline: false,
      status: result.status,
      slugs: [],
      rows: [],
      tier: DEFAULT_TIER,
      limit: DEFAULT_LIMIT,
    };
  }

  const data = result.data;
  // The endpoint answers with { email, tier, limit, subscriptions: [...] }; a
  // bare array is accepted too so a shape change on the backend cannot blank
  // the UI — in that case tier/limit fall back to the free defaults below.
  const rows = Array.isArray(data) ? data : Array.isArray(data?.subscriptions) ? data.subscriptions : [];
  const slugs = rows.map((row) => row?.series_slug).filter((slug) => typeof slug === 'string');
  const tier = data && typeof data === 'object' && typeof data.tier === 'string' ? data.tier : DEFAULT_TIER;
  // `limit: null` means unlimited and must survive; only a genuinely missing
  // field falls back to the free plan's 1.
  const hasLimit = Boolean(data) && typeof data === 'object' && !Array.isArray(data) && 'limit' in data;
  const limit = hasLimit ? (data.limit === null ? null : Number(data.limit)) : DEFAULT_LIMIT;
  return { ok: true, offline: false, slugs, rows, tier, limit };
}

/**
 * Claim a series for the signed-in subscriber. The one-free-pick rule lives
 * on the server: a second pick and a repeat pick both come back as 409 with
 * a Swedish `detail` for the form.
 */
export async function createSubscription(seriesSlug) {
  const result = await authedSend('POST', { series_slug: seriesSlug }, SUBSCRIPTIONS_URL);
  if (result.ok) return { ok: true };
  if (result.offline) return { ok: false, offline: true, message: '' };
  return {
    ok: false,
    offline: false,
    status: result.status,
    message: detailOf(result.data, 'Bevakningen kunde inte aktiveras. Försök igen.'),
  };
}

/**
 * Give a series back. A 404 means the server already agrees it is gone, which
 * is the state the caller wanted, so it counts as success. `frequency` is
 * optional — only an owned feed's row needs it to disambiguate which
 * subscription to drop — so every existing call site keeps working unchanged.
 */
export async function deleteSubscription(seriesSlug, frequency) {
  const body = frequency ? { series_slug: seriesSlug, frequency } : { series_slug: seriesSlug };
  const result = await authedSend('DELETE', body, SUBSCRIPTIONS_URL);
  if (result.ok || result.status === 404) return { ok: true };
  if (result.offline) return { ok: false, offline: true, message: '' };
  return {
    ok: false,
    offline: false,
    status: result.status,
    message: detailOf(result.data, 'Bevakningen kunde inte tas bort. Försök igen.'),
  };
}

/**
 * Add the reader's own RSS/Atom feed as a private series, subscribed to it
 * in the same call (T6.1's `POST /api/v1/series`) for the signed-in
 * subscriber. Never throws — same contract as the rest of this file.
 * Returns { ok: true, series } (the series object the server created) or
 * { ok: false, offline, status, message } with the server's Swedish `detail`
 * verbatim (no feed found, unsafe address, already has one own feed on the
 * free plan, bad frequency, ...).
 */
export async function createOwnFeed(feedUrl, frequency) {
  const result = await authedSend('POST', { feed_url: feedUrl, frequency }, SERIES_URL);
  if (result.ok) return { ok: true, series: result.data };
  if (result.offline) return { ok: false, offline: true, status: 0, message: '' };
  return {
    ok: false,
    offline: false,
    status: result.status,
    message: detailOf(result.data, 'Sidan kunde inte läggas till. Försök igen.'),
  };
}

/**
 * Ask for a login link. Never reveals whether the address is known — a
 * well-formed email always gets 202 — so the only refusals a caller sees are
 * a bad email/series/frequency (422) or too many requests in an hour (429).
 * `intent` (`{ series_slug, frequency? }`) is the catalogue pick to activate
 * the moment the link is clicked, for a first-time reader who picked a
 * series before signing in.
 */
export async function requestLoginLink(email, intent) {
  const body = intent ? { email, ...intent } : { email };
  const result = await send('POST', body, AUTH_LINK_URL);
  if (result.ok) return { ok: true };
  if (result.offline) return { ok: false, offline: true, message: '' };
  return {
    ok: false,
    offline: false,
    status: result.status,
    message: detailOf(result.data, 'Länken kunde inte skickas. Försök igen.'),
  };
}

/**
 * Exchange a one-time login token (the query string of /logga-in/?token=…)
 * for a session. 400 covers an expired, used or unknown link.
 */
export async function verifyLoginLink(token) {
  const result = await send('POST', { token }, AUTH_VERIFY_URL);
  if (result.ok) {
    return {
      ok: true,
      token: result.data?.token,
      email: result.data?.email,
      tier: result.data?.tier,
      limit: result.data?.limit,
    };
  }
  if (result.offline) return { ok: false, offline: true, message: '' };
  return {
    ok: false,
    offline: false,
    status: result.status,
    message: detailOf(result.data, 'Länken kunde inte användas.'),
  };
}

/** GET /auth/me with the stored bearer. 401 forgets the stored session. */
export async function fetchMe() {
  const result = await authedGet(AUTH_ME_URL);
  if (result.ok) {
    return { ok: true, email: result.data?.email, tier: result.data?.tier, limit: result.data?.limit };
  }
  if (result.offline) return { ok: false, offline: true, status: 0 };
  return { ok: false, offline: false, status: result.status };
}

/**
 * POST /auth/logout. Best effort: whether or not this reaches the server,
 * the caller (Shell.jsx's logout()) forgets the session locally either way —
 * a reader can always sign out of their own browser, online or not.
 */
export async function logoutSession() {
  const result = await authedSend('POST', undefined, AUTH_LOGOUT_URL);
  return { ok: result.ok };
}

/**
 * The reader's own latest edition for one series, for the Utskick tab's
 * per-watch card. Public endpoint — same GET /editions the sample and digest
 * pages could use, just filtered to one series and the newest row.
 *
 * Never resolves to an error: an empty result (nothing sent yet), a refusal,
 * or an unreachable server are all the same thing to a reader who has not
 * had a delivery yet, so they all come back as `{ ok: true, edition: null }`.
 * A caller that wants to fetch several slugs in parallel can just
 * Promise.all() this — nothing here ever rejects.
 */
export async function fetchLatestEdition(seriesSlug) {
  const url = `${EDITIONS_URL}?series=${encodeURIComponent(seriesSlug)}&limit=1`;
  let response;
  try {
    response = await fetch(url);
  } catch {
    return { ok: true, edition: null };
  }
  if (!response.ok) return { ok: true, edition: null };
  const data = await readJson(response);
  const rows = Array.isArray(data) ? data : [];
  return { ok: true, edition: rows[0] || null };
}

/**
 * "Skicka till mig": re-send the reader's own copy of an edition they
 * already watch. Needs a session — the server resends to whichever address
 * the session belongs to, not one this call names. 404 covers both an
 * unknown slug and an edition that was never sent (see the route's own
 * docstring), which reads the same to a subscriber either way: "we can't
 * send you that one".
 */
export async function sendEditionToMe(editionSlug) {
  const result = await authedSend(
    'POST',
    undefined,
    `${EDITIONS_URL}/${encodeURIComponent(editionSlug)}/send-to-me`
  );
  if (result.ok) return { ok: true, email: result.data?.email };
  if (result.offline) return { ok: false, offline: true, status: 0, message: '' };
  return {
    ok: false,
    offline: false,
    status: result.status,
    message: detailOf(result.data, 'Utskicket kunde inte skickas. Försök igen.'),
  };
}

/**
 * Ask for a source we do not watch yet — either the page whose auto-add just
 * failed (`url`), or a publisher named in free text (`note`), or both. Same
 * never-throws contract as the rest of this file.
 * Returns { ok: true } or { ok: false, offline, status, message } with the
 * server's Swedish `detail` verbatim (empty url+note, bad email, too many
 * requests, ...).
 */
export async function createSeriesRequest(email, url, note) {
  const body = { email };
  if (url) body.url = url;
  if (note) body.note = note;
  const result = await send('POST', body, SERIES_REQUESTS_URL);
  if (result.ok) return { ok: true };
  if (result.offline) return { ok: false, offline: true, status: 0, message: '' };
  return {
    ok: false,
    offline: false,
    status: result.status,
    message: detailOf(result.data, 'Önskemålet kunde inte skickas. Försök igen.'),
  };
}
